import React, { useEffect, useMemo, useState } from "react";
import { Modal, Card, Stack, Skeleton, List, ListItemButton, Chip } from "@mui/material";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import KendoInput from "assets/theme/components/inputKendo";
import { ServicesApiService } from "api/services.api";

const containerStyles = {
  width: { xs: "96%", md: "60%", lg: "45%" },
  maxHeight: "82vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: 2,
  p: 3,
};

const AddServicePickerModal = ({ open, handleClose, onAddItem }) => {
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [quantity, setQuantity] = useState("1");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await ServicesApiService.list({
          keyword,
          status: 1,
          pageNumber: 1,
          pageSize: 10,
        });
        if (cancelled) return;
        setServices(response?.data?.data?.data ?? []);
      } catch (error) {
        if (cancelled) return;
        toast.error(error?.response?.data?.message || "Failed to load services");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [open, keyword]);

  useEffect(() => {
    if (!open) {
      setKeyword("");
      setQuantity("1");
      setSelected(null);
      setServices([]);
    }
  }, [open]);

  const rate = useMemo(() => Number(selected?.standardRate ?? 0), [selected]);
  const qtyNum = Math.max(0, Number(quantity) || 0);

  const handleAdd = () => {
    if (!selected) {
      toast.error("Pick a service first.");
      return;
    }
    if (qtyNum <= 0) {
      toast.error("Quantity must be greater than zero.");
      return;
    }
    onAddItem({
      lineKind: "service",
      serviceId: selected.id,
      name: selected.name,
      itemNo: `SVC-${selected.id}`,
      description: selected.description || "",
      quantity: String(qtyNum),
      rate: String(rate),
      price: String(rate),
      serviceUnitOfMeasure: selected.unitOfMeasure,
    });
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <SoftBox sx={containerStyles}>
        <Card sx={{ p: 2, boxShadow: "none" }}>
          <SoftTypography variant="h5" mb={2}>
            Add Service to Quotation
          </SoftTypography>

          <KendoInput
            placeholder="Search services by name…"
            type="search"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setSelected(null);
            }}
          />

          <SoftBox mt={2} sx={{ minHeight: 240 }}>
            {loading ? (
              <Skeleton variant="rectangular" height={240} animation="wave" />
            ) : services.length === 0 ? (
              <SoftBox p={2} textAlign="center">
                <SoftTypography variant="caption">
                  No active services. Add some in Catalog → Services first.
                </SoftTypography>
              </SoftBox>
            ) : (
              <List dense sx={{ maxHeight: 280, overflowY: "auto", border: "1px solid #eee", borderRadius: 1 }}>
                {services.map((s) => {
                  const isSelected = selected?.id === s.id;
                  return (
                    <ListItemButton
                      key={s.id}
                      selected={isSelected}
                      onClick={() => setSelected(s)}
                      sx={{
                        borderBottom: "1px solid #f0f0f0",
                        "&.Mui-selected": { backgroundColor: "#E3F2FD" },
                      }}
                    >
                      <SoftBox flex={1}>
                        <SoftBox display="flex" alignItems="center" gap={1}>
                          <SoftTypography variant="body2" fontWeight="medium" fontSize={13}>
                            {s.name}
                          </SoftTypography>
                          <Chip
                            label="Labor"
                            size="small"
                            sx={{
                              height: 18,
                              backgroundColor: "#E8F0FE",
                              color: "#1A56C0",
                              fontWeight: 600,
                              fontSize: 10,
                            }}
                          />
                        </SoftBox>
                        <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                          ${s.standardRate ?? 0} per {s.unitOfMeasure || "unit"}
                          {s.description ? ` — ${s.description}` : ""}
                        </SoftTypography>
                      </SoftBox>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </SoftBox>

          {selected && (
            <SoftBox mt={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <SoftBox flex={1}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Quantity ({selected.unitOfMeasure || "units"})
                  </SoftTypography>
                  <KendoInput
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                </SoftBox>
                <SoftBox flex={1}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Rate
                  </SoftTypography>
                  <SoftBox
                    sx={{
                      bgcolor: "#fafafa",
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                      border: "1px solid #eee",
                    }}
                  >
                    <SoftTypography variant="body2">${rate.toFixed(2)}</SoftTypography>
                  </SoftBox>
                </SoftBox>
              </Stack>
            </SoftBox>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end" mt={3}>
            <SoftButton variant="outlined" onClick={handleClose}>
              Cancel
            </SoftButton>
            <SoftButton color="success" variant="contained" onClick={handleAdd} disabled={!selected}>
              Add Service
            </SoftButton>
          </Stack>
        </Card>
      </SoftBox>
    </Modal>
  );
};

AddServicePickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onAddItem: PropTypes.func.isRequired,
};

export default AddServicePickerModal;
